import type { HpSearchCommuteMode, HpSearchCommuteTime } from '@zg-rentals/ts-hotpads-api-client';
import type { CommuteFilter, CommuteMinutes, CommuteTimeMode, CommuteTravelMode } from '../filterTypes';

export const COMMUTE_MINUTES_OPTIONS: ReadonlyArray<CommuteMinutes> = [15, 30, 45, 60];

const VALID_COMMUTE_MINUTES = new Set(COMMUTE_MINUTES_OPTIONS.map(String));
const VALID_COMMUTE_TIME_MODES = new Set<string>(['now', 'rushHour', 'offPeak'] satisfies Array<CommuteTimeMode>);

const MINUTE_MS = 60_000;

// String-literal unions of the generated enums (same pattern as SearchFlag in
// filterStateToV3.ts) so the maps stay literal; the single cast to the nominal
// DTO happens at the filterStateToV3 boundary.
type CommuteModeV3 = `${HpSearchCommuteMode}`;
type CommuteTimeV3 = `${HpSearchCommuteTime}`;

// FE travel mode (Google Maps vocabulary) ↔ legacy URL value (Java enum, CYCLING
// not BICYCLING). URL values double as the legacy V2 API's commuteMode.
const MODE_TO_URL: Record<CommuteTravelMode, string> = {
  DRIVING: 'DRIVING',
  TRANSIT: 'TRANSIT',
  WALKING: 'WALKING',
  BICYCLING: 'CYCLING',
};
const URL_TO_MODE: Record<string, CommuteTravelMode> = {
  DRIVING: 'DRIVING',
  TRANSIT: 'TRANSIT',
  WALKING: 'WALKING',
  CYCLING: 'BICYCLING',
};

// FE travel mode → V3 wire enum.
const MODE_TO_V3: Record<CommuteTravelMode, CommuteModeV3> = {
  DRIVING: 'car',
  TRANSIT: 'transit',
  WALKING: 'foot',
  BICYCLING: 'bike',
};

export function commuteModeToUrl(mode: CommuteTravelMode): string {
  return MODE_TO_URL[mode];
}

/** Invalid or missing URL values fall back to the legacy default, DRIVING. */
export function parseCommuteModeFromUrl(value: string | undefined): CommuteTravelMode {
  return (value && URL_TO_MODE[value]) || 'DRIVING';
}

/** Invalid or missing URL values fall back to the legacy default, 'now'. */
export function parseCommuteTimeModeFromUrl(value: string | undefined): CommuteTimeMode {
  return value && VALID_COMMUTE_TIME_MODES.has(value) ? (value as CommuteTimeMode) : 'now';
}

/** Minutes are the hard gate: invalid or missing means the whole filter is off. */
export function parseCommuteMinutesFromUrl(value: string | undefined): CommuteMinutes | null {
  return value && VALID_COMMUTE_MINUTES.has(value) ? (Number(value) as CommuteMinutes) : null;
}

/**
 * Maps the FE commute filter to the V3 `filters.commute` shape.
 * `maxDuration` is a RAW millisecond number on the wire — the generated
 * MilliDuration `{ millis }` object form 500s (same Datarouter quirk as
 * `activatedWithin` in filterStateToV3.ts), so the return type stays
 * structural and the caller casts once at the DTO boundary.
 */
export function commuteToV3(commute: CommuteFilter): {
  coordinate: { lat: number; lon: number };
  commuteMode: CommuteModeV3;
  commuteTime: CommuteTimeV3;
  maxDuration: number;
} {
  return {
    coordinate: { lat: commute.lat, lon: commute.lon },
    commuteMode: MODE_TO_V3[commute.mode],
    commuteTime: commute.timeMode,
    maxDuration: commute.minutes * MINUTE_MS,
  };
}

/**
 * Legacy V2 flat query params (dot map, byCoords). Param names match the
 * legacy hotpads-web URL/API vocabulary: scalar values despite the plural
 * spelling. Saved searches intentionally exclude commute (matching legacy
 * alerts) by overriding `commute: null` before calling toApiParams — see
 * savedSearches.ts.
 */
export function commuteToV2Params(commute: CommuteFilter | null): Record<string, string | number> {
  if (!commute) return {};
  return {
    commuteLats: commute.lat,
    commuteLons: commute.lon,
    commuteMode: MODE_TO_URL[commute.mode],
    commuteTime: String(commute.minutes),
    commuteTimeMode: commute.timeMode,
  };
}
